import * as React from 'react';

import { emptyObject } from 'bones/constants/empty';
import { MimeType } from 'bones/constants/mimeTypes';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxPanelData } from 'bones/constants/voicebox/panel';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { useWithLoading } from 'bones/utils/hooks/useWithLoading';
import { makeStardogQueryName } from 'bones/utils/voicebox/stardogQuery';
import {
  QueryArgs,
  ResultBindings,
  usePortalVoiceboxStardogQuery,
} from 'bones/utils/voicebox/usePortalVoiceboxStardogQuery';

const getInstancesQueryArgs = (
  iri: string,
  limit: number,
  offset: number
): QueryArgs<ResultBindings<{ subject: string }>, string[]> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Instances', `${iri} (${limit}, ${offset})`)}
SELECT DISTINCT
  ?subject
WHERE {
  ?subject a <${iri}> .
}
LIMIT ${limit}
OFFSET ${offset}
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  parseBody: async (body) => {
    const bindings = body?.results?.bindings || [];
    return bindings.reduce<string[]>((acc, binding) => {
      if (binding.subject?.value) {
        acc.push(binding.subject.value);
      }
      return acc;
    }, []);
  },
});

const getInstancesCountQueryArgs = (
  iri: string
): QueryArgs<ResultBindings<{ count: string }>, number> => ({
  query: `\
# ${makeStardogQueryName('Voicebox Instances Count', iri)}
SELECT
  (count(DISTINCT ?subject) as ?count)
WHERE {
  ?subject a <${iri}> .
}
`,
  queryResultsMime: MimeType.JSON_SPARQL_RESULTS,
  parseBody: async (body) => {
    const bindings = body?.results?.bindings || [];
    return parseInt(bindings[0]?.count?.value || '0', 10);
  },
});

export const usePortalVoiceboxInstancesFetcher = (
  connectionIdx: number,
  limit: number,
  settingsContext: VoiceboxSettingsContextValue,
  fetchLabels: (iris: string[]) => Promise<void>,
  setInstancesForIri: (
    iri: string,
    action: React.SetStateAction<VoiceboxPanelData['instances']>
  ) => void
) => {
  const { modelsByDatabaseId, settings } = settingsContext;
  const modelsById = modelsByDatabaseId[settings.databaseId] || emptyObject;
  const { classesByIri } = modelsById[settings.model || DEFAULT_MODEL_NAME] || {
    classesByIri: emptyObject,
  };

  const isMounted = useIsMounted();
  const withLoading = useWithLoading('usePortalVoiceboxInstancesFetcher');

  const fetchInstances = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getInstancesQueryArgs
  );

  const fetchInstancesCount = usePortalVoiceboxStardogQuery(
    connectionIdx,
    settingsContext,
    getInstancesCountQueryArgs
  );

  const fetchWithOffset = React.useCallback(
    async (iri: string, offset: number) => {
      const isInstance = Boolean(!classesByIri[iri]);
      if (isInstance) {
        return null;
      }

      return withLoading(
        (loading) =>
          setInstancesForIri(iri, (prev) => ({
            ...prev,
            ...(offset ? { loadingMore: loading } : { loading }),
          })),
        async () => {
          if (!offset) {
            const count = await fetchInstancesCount(iri);
            if (!count || !isMounted.current) {
              return null;
            }

            setInstancesForIri(iri, (prev) => ({
              ...prev,
              count,
            }));
          }

          const newInstances = await fetchInstances(iri, limit, offset);
          if (!newInstances?.length || !isMounted.current) {
            return null;
          }

          // fetch labels first to avoid displaying local name before label is loaded
          await fetchLabels(newInstances);

          setInstancesForIri(iri, (prev) => {
            const items = [...(offset ? prev.items : []), ...newInstances];
            const hasMore = prev.count > items.length;
            return {
              ...prev,
              items,

              hasMore,
              loadMore: async () => {
                if (hasMore) {
                  await fetchWithOffset(iri, items.length);
                }
              },
            };
          });

          return newInstances;
        }
      );
    },
    [
      classesByIri,
      fetchInstances,
      fetchInstancesCount,
      fetchLabels,
      isMounted,
      limit,
      setInstancesForIri,
      withLoading,
    ]
  );

  return fetchWithOffset;
};
